import { useContext, useEffect, useRef, useState } from 'react';
import GuestVerifyModal from './GuestVerifyModal';
import { useAppContext } from '../../hooks';
import router, { useRouter } from 'next/router';
import { PUBLIC_PAGES } from '../../utils/constant';
import { SnackbarKey, useSnackbar } from 'notistack';
import { Button, IconButton } from '@mui/material';
import { CloseIcon } from 'libs/ui-base/src/lib/theme/overrides/CustomIcons';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';

/**
 * GuestVerifyGate
 * Renders the GuestVerifyModal on every page load while the current user is a guest.
 * The modal can be temporarily dismissed (onClose) but will reappear on next navigation / refresh
 * until the backend reports user.isGuest === false.
 */
export function GuestVerifyGate() {
  const { isUserSubscribed } = useContext(CreditContext);
  const { userData, isUserDataLoading } = useAppContext();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [lastManualClose, setLastManualClose] = useState<number | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isOpenRef = useRef(false);
  const EMAIL_SNACKBAR_KEY = 'email-sent-snackbar';

  // derive flags synchronously so hook order is stable
  const isGuest = !!(userData as any)?.isGuest;
  const isPublicPage = PUBLIC_PAGES.includes(router.pathname);

  const show = (email) => {
    if (isOpenRef.current) return;

    enqueueSnackbar(
      `Almost there! We've sent a password setup link to ${email}. Please set your password to keep your account safe and accessible.`,
      {
        key: EMAIL_SNACKBAR_KEY,
        variant: 'info',
        persist: true, // 👈 stays until closed manually
        action: (key: SnackbarKey) => (
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                // resend logic here
                console.log('Resend clicked');
                setOpen(true);
              }}
            >
              Didn't Get It?
            </Button>

            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                closeSnackbar(key);
                isOpenRef.current = false;
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ),
        onClose: () => {
          isOpenRef.current = false;
        },
      }
    );

    isOpenRef.current = true;
  };

  // Open whenever user is a guest and data finished loading and not on public pages
  useEffect(() => {
    if (!isUserDataLoading && isGuest && !isPublicPage && isUserSubscribed) {
      // setOpen(true);
      show(userData.email);
    } else {
      setOpen(false);
    }
  }, [isGuest, isUserDataLoading, isPublicPage, isUserSubscribed]);

  // Re-open on route change if still guest and not on public pages
  useEffect(() => {
    const handleRoute = () => {
      if ((userData as any)?.isGuest && !PUBLIC_PAGES.includes(router.pathname)) {
        // setOpen(true);
        // show(userData.email);
      }
    };
    router.events.on('routeChangeComplete', handleRoute);
    return () => {
      router.events.off('routeChangeComplete', handleRoute);
    };
  }, [router.events, userData]);

  // Periodic reopen every 2 minutes while guest (even if user dismissed it) and not on public pages
  // useEffect(() => {
  //   if (!isGuest || isPublicPage) return;
  //   const interval = setInterval(() => {
  //     setOpen(true);
  //   }, 120000); // 2 minutes
  //   return () => clearInterval(interval);
  // }, [isGuest, isPublicPage]);

  // Final render: only show modal when user is guest and not on public pages
  if (!isGuest || isPublicPage) return null;

  return (
    <GuestVerifyModal
      open={open}
      onClose={() => {
        setOpen(false);
        setLastManualClose(Date.now());
      }}
    />
  );
}

export default GuestVerifyGate;
